import { makeSaga } from '../util'
import { call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects'
import fp from 'lodash/fp'

import { types, actions } from '../../reducers/matches'
import { types as authTypes } from '../../reducers/auth'
import { actions as ghostUsersActions, types as ghostUsersTypes } from '../../reducers/ghostUsers'
import { actions as flightsActions } from '../../reducers/matches/flights'
import { actions as gamesActions } from '../../reducers/matches/games'
import { actions as matchesActions, selectors as matchesSelectors } from '../../reducers/matches/matches'
import { actions as shotsActions } from '../../reducers/shots'
import { actions as buddiesActions } from '../../reducers/buddies'
import { types as metronomeTypes } from '../../reducers/metronome'
import { postEffect } from './../network'

//  ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
//  ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
//  █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
//  ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
//  ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
//  ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝

export function * syncMatchesEffect (body) {
  const request = {
    url: 'matches',
    options: {
      body
    }
  }
  const chan = yield call(postEffect, request)
  return yield take(chan)
}

export function * mergeMatchesFlightsGamesBuddies (matchesFromAbove) {
  const matches = fp.get('matches', matchesFromAbove)
  yield put(matchesActions.merge({ matches }))

  const flights = fp.get('flights', matchesFromAbove)
  yield put(flightsActions.merge({ flights }))

  const games = fp.get('games', matchesFromAbove)
  yield put(gamesActions.merge({ games }))
  yield put(shotsActions.merge(games))

  const buddies = fp.get('buddies', matchesFromAbove)
  yield put(buddiesActions.set(buddies))
}

//  ███████╗ █████╗  ██████╗  █████╗ ███████╗
//  ██╔════╝██╔══██╗██╔════╝ ██╔══██╗██╔════╝
//  ███████╗███████║██║  ███╗███████║███████╗
//  ╚════██║██╔══██║██║   ██║██╔══██║╚════██║
//  ███████║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

export function * syncMatchesFlow () {
  yield put(matchesActions.markSyncStarted())

  yield put(ghostUsersActions.syncRequest())
  const ghostUsersSyncStatus = yield take([ghostUsersTypes.SYNC_ERROR, ghostUsersTypes.SYNC_SUCCESS])

  if (ghostUsersSyncStatus.type === ghostUsersTypes.SYNC_ERROR) {
    yield put(matchesActions.markSyncFailed())
  } else {
    // mock payload since we can't actually change anything anyhow
    const timestamp = yield select(matchesSelectors.getTimestamp)
    const matchesPayload = {
      matches: [],
      flights: [],
      games: [],
      timestamp
    }
    const { error, ...matchesFromAbove } = yield call(syncMatchesEffect, matchesPayload)

    if (error) {
      yield put(matchesActions.markSyncFailed())
    } else {
      const timestamp = fp.get('timestamp', matchesFromAbove)
      yield put(matchesActions.setTimestamp({ timestamp }))

      yield call(mergeMatchesFlightsGamesBuddies, matchesFromAbove)

      yield put(matchesActions.markSyncSucceeded())
    }
  }

  yield put(matchesActions.markSyncFinished())
}

export function * syncMatchesProxy () {
  yield put(actions.sync())
}

export function * clearMatchesFlow () {
  yield [
    put(matchesActions.clear()),
    put(flightsActions.clear()),
    put(gamesActions.clear()),
    put(shotsActions.clear())
  ]
}

function * bootstrapFlow () {
  yield put(matchesActions.setTimestamp({ timestamp: undefined }))
}

export default makeSaga([
  takeLatest(types.SYNC, syncMatchesFlow),
  takeLatest(types.CLEAR, clearMatchesFlow),
  takeLatest(authTypes.BOOTSTRAP_REQUEST, bootstrapFlow),
  takeEvery(metronomeTypes.TICK_MIN_5, syncMatchesProxy)
])
